import styled from "styled-components";
import FixaStatus from "@coworker/apprestructured/src/shared/wrappers/FixaStatus/FixaStatus";
import { useTranslation } from "@coworker/locales";
import { TaskLinks } from "../types/taskLink";
import { AlertResponse, MediaResponse } from "../types/media";
import { TabIds, TabQueryKey } from "../Organisms/MediaTabs.helper";
import {
  CenterContainer,
  Column,
  FullLine,
  LeftContainer,
  Row,
} from "../styles/styles";
import FixaImage from "@coworker/apprestructured/src/shared/wrappers/FixaImage/FixaImage";
import { MediaTypes } from "../constants";
import { Text } from "../Atoms/Text";
import {
  getEDSAlertCount,
  getOngoingTaskCount,
  getSalesStopAlertCount,
} from "./MediaListItem.helper";
import { getMediaTypeText } from "../tools";
import { ConditionalAnchor } from "./ConditionalAnchor";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";

const SolidStatus = styled(FixaStatus)`
  .status__dot {
    background-color: currentColor;
  }

  font-size: 12px;
`;

const LeftContainerWithMargin = styled(LeftContainer)`
  margin: 0px 16px;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  overflow: hidden;

  .image--fallback {
    background-color: var(--grey100);
  }
`;

const MediaName = styled.div`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 700;
  color: var(--black);
  font-size: 14px;
`;

const StyledImage = styled(FixaImage)<{ rotation: number }>`
  object-fit: cover;
  object-position: center center;
  transform: rotate(${({ rotation }) => rotation}deg);
  height: 100%;
  width: 100%;
`;

const StatusRow = styled(Row)`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  font-size: 12px;
`;

const Container = styled.div`
  padding: 24px 0px;
  width: 100%;
  background-color: var(--white);
`;

const ArticleCountText = styled.div`
  font-size: 14px;
  color: #484848;
  line-height: 22px;
  width: 100%;
`;

const ArticleInfoBarContainer = styled.div`
  display: block;
  float: left;
  width: 100%;
  height: 22px;
  margin-top: 8px;
`;

const ArticleInfoTextBox = styled.div<{
  $color: string;
  $backgroundColor: string;
}>`
  display: block;
  float: left;
  color: ${({ $color }) => $color};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  padding: 2px 8px;
`;

interface MediaListItemProps {
  mediaResponse: MediaResponse;
  taskLinks: TaskLinks;
  alerts: AlertResponse[];
}

export function MediaListItemMobile({
  mediaResponse,
  taskLinks,
  alerts,
}: MediaListItemProps) {
  const { t } = useTranslation();
  const navigate = useFixaNavigate();

  const ongoingTaskCount = getOngoingTaskCount(
    taskLinks,
    mediaResponse.media.id
  );
  const edsAlertCount = getEDSAlertCount(alerts, mediaResponse.media.id);
  const salesStopAlertCount = getSalesStopAlertCount(
    alerts,
    mediaResponse.media.id
  );

  return (
    <>
      <ConditionalAnchor
        href={`/mediamaintenance/tabs/${mediaResponse.media.id}?${TabQueryKey}=${TabIds.LIST}`}
        onClick={() => {
          navigate(
            `/mediamaintenance/tabs/${mediaResponse.media.id}?${TabQueryKey}=${TabIds.LIST}`
          );
        }}
        isAnchor={true}
      >
        <Container>
          <Row>
            <LeftContainerWithMargin>
              <ImageContainer>
                <StyledImage
                  src={mediaResponse.media.mainImageUrl ?? ""}
                  alt={mediaResponse.media.mainImageUrl ? "" : "No image"}
                  rotation={mediaResponse.media.mainImageRotation ?? 0}
                  loading="eager"
                  fallback={!mediaResponse.media.mainImageUrl}
                />
              </ImageContainer>
            </LeftContainerWithMargin>
            <Column>
              <CenterContainer $centerText={false}>
                <StatusRow>
                  <Text
                    size={"small"}
                    text={getMediaTypeText(
                      mediaResponse.media.mediaType as MediaTypes
                    )}
                  />
                  &nbsp;&nbsp;
                  {mediaResponse.media.active === "INACTIVE" && (
                    <SolidStatus
                      label={t("inactiveString")}
                      statusColor="red"
                      small={true}
                    />
                  )}
                  {mediaResponse.media.active === "ACTIVE" && (
                    <SolidStatus
                      label={t("activeString")}
                      statusColor="green"
                      small={true}
                    />
                  )}
                </StatusRow>
              </CenterContainer>
              <CenterContainer $centerText={false}>
                <MediaName>{mediaResponse.media.name}</MediaName>
                <ArticleCountText>
                  {mediaResponse.media.mediaType === MediaTypes.HOME
                    ? t("countRoomsString", {
                        count: mediaResponse.children?.filter(
                          (c) => c.parentMediaId === mediaResponse.media.id
                        ).length,
                      })
                    : t("countArticlesString", {
                        count: parseInt(
                          `${mediaResponse.media?.nbrArticles ?? 0}`
                        ),
                      })}
                </ArticleCountText>
                <ArticleInfoBarContainer>
                  {salesStopAlertCount > 0 && (
                    <ArticleInfoTextBox
                      $color="#E00751"
                      $backgroundColor="#FCE6EE"
                    >{`${salesStopAlertCount} Sale stop`}</ArticleInfoTextBox>
                  )}
                  {edsAlertCount > 0 && (
                    <ArticleInfoTextBox
                      $color="#CA5008"
                      $backgroundColor="#F2BEA7"
                    >{`${edsAlertCount} EDS`}</ArticleInfoTextBox>
                  )}
                  {ongoingTaskCount > 0 && (
                    <ArticleInfoTextBox
                      $color="#0A8A00"
                      $backgroundColor="#E6F3E5"
                    >{`${ongoingTaskCount} Tasks`}</ArticleInfoTextBox>
                  )}
                </ArticleInfoBarContainer>
              </CenterContainer>
            </Column>
          </Row>
        </Container>
        <FullLine margin={"0"} />
      </ConditionalAnchor>
    </>
  );
}
